<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ loading ? '...' : booking.date }}</ion-title>
        <ion-buttons slot="start">
          <ion-back-button default-href="/bookings"></ion-back-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <!-- Loading Data -->
      <div class="spin" v-if="loading">
        <ion-spinner></ion-spinner>
      </div>

      <!-- Data loaded -->
      <ion-grid fixed v-if="!loading && booking">
        <ion-row>
          <ion-col size="12">
            <ion-card class="ion-text-center">
              <ion-row>
                <ion-col>
                  <ion-card-header>
                    <ion-card-title color="medium">{{ t('bookingDetails') }}</ion-card-title>
                    <ion-card-title color="primary">{{ booking.date }} {{ booking.startTime.split(":").slice(0, -1).join(":") }}</ion-card-title>
                    <ion-card-title><ion-chip><ion-label>{{ booking.duration }} {{ t('minutes') }}</ion-label></ion-chip></ion-card-title>
                  </ion-card-header>
                  <ion-card-content>
                    <ion-badge :color="getBookingStatusColor(booking.status)">{{ t(booking.status) }}</ion-badge>
                    <p class="ion-padding" style="white-space: pre-wrap;" v-if="booking.providedServices">
                      {{ t('bookedServices') }}{{ booking.providedServices.split(" , ").map(s => s.split(" - ")[0]).join(" , ") }}
                    </p>
                    <p class="ion-padding" style="white-space: pre-wrap;" v-if="booking.customerNote">
                      {{ t('remark') }}: {{ booking.customerNote }}
                    </p>
                    <ion-button class="ion-margin-bottom" expand="block" @click.stop="showBookingQRCode(booking)" v-if="!isBookingCompleted(booking)">
                      <ion-icon slot="start" :icon="qrCodeOutline"></ion-icon>
                      {{ t('bookingQRCode') }}
                    </ion-button>
                    <ion-button color="tertiary" class="ion-margin-bottom" expand="block" @click.stop="updateBooking(booking)" v-if="canUpdateBooking(booking)">
                      <ion-icon slot="start" :icon="calendarOutline"></ion-icon>
                      {{ t('changeBookingTime') }}
                    </ion-button>
                    <ion-button color="danger" expand="block" @click.stop="cancelBooking(booking)" v-if="canCancelBooking(booking)">
                      <ion-icon slot="start" :icon="close"></ion-icon>
                      {{ t('cancelBooking') }}
                    </ion-button>

                    <div v-if="isBookingCompleted(booking)">
                      <ion-title class="ion-text-center">{{ t('beforeTreatment') }}</ion-title>
                      <ion-row>
                        <ion-col size-xs="6" size-md="4" :key="photoLink" v-for="photoLink in booking.beforePhotoLinks.split(';')">
                          <ion-img v-if="photoLink" :src="photoLink"></ion-img>
                        </ion-col>
                      </ion-row>
                      <ion-title class="ion-text-center">{{ t('afterTreatment') }}</ion-title>
                      <ion-row>
                        <ion-col size-xs="6" size-md="4" :key="photoLink" v-for="photoLink in booking.afterPhotoLinks.split(';')">
                          <ion-img v-if="photoLink" :src="photoLink"></ion-img>
                        </ion-col>
                      </ion-row>
                    </div>
                    <p class="ion-padding-top"><small>{{ t('PostPage.lastUpdated') }}{{ formatDate(booking.updatedAt) }}</small></p>
                  </ion-card-content>
                </ion-col>
              </ion-row>
            </ion-card>
          </ion-col>

        </ion-row>
      </ion-grid>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
// Vue reactivity
import { computed } from "vue";

// icons
import { star, starHalfOutline, starOutline, calendarOutline, pencil, close, qrCodeOutline } from "ionicons/icons";

// components
import { IonPage, IonHeader, IonToolbar, IonBackButton, IonTitle, IonContent,
        IonGrid, IonSpinner, IonRow, IonCol, IonButtons, IonButton,
        IonCard, IonCardHeader, IonCardSubtitle, IonCardTitle, IonCardContent,
        IonIcon, IonAvatar, IonLabel, IonItem, IonChip, IonNote, IonBadge, IonImg,
        loadingController, alertController } from "@ionic/vue";

// API services
import BookingService from '@/services/BookingService';

import { utils } from '@/composables/utils';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

export default {
  name: "BookingDetailPage",
  components: {
    IonPage, IonHeader, IonToolbar, IonBackButton, IonTitle, IonContent,
    IonGrid, IonSpinner, IonRow, IonCol, IonButtons, IonButton,
    IonCard, IonCardHeader, IonCardSubtitle, IonCardTitle, IonCardContent,
    IonIcon, IonAvatar, IonLabel, IonItem, IonChip, IonNote, IonBadge, IonImg,
  },
  setup() {
    // methods or filters
    const store = useStore();
    const { t } = useI18n();
    const { BOOKING_STATUSES, getBookingStatusColor, formatDate, presentToast } = utils();
    const route = useRoute();
    const currId = route.params.id;

    // 1. declare state variables (ref to make them reactive)
    const loading = computed(() => store.state.loadingBookings);
    const booking = computed(() => store.getters.getBookingById(currId));

    const cancelBooking = async (booking: any) => {
      const alert = await alertController.create({
        header: t('confirmCancel'),
        message: t('confirmCancelBooking'),
        buttons: [
          {
            text: t('cancel'),
            role: 'cancel',
            cssClass: 'secondary'
          },
          {
            text: t('cancelBooking'),
            handler: async () => {
              const loading = await loadingController.create({});
              await loading.present();
              const res = await BookingService.cancelBooking(booking.id);
              store.commit('updateBooking', res[0]);
              loading.dismiss();
              presentToast( t('bookingCancelled'), 2000 );
            },
          },
        ],
      });
      return alert.present();
    }

    const canUpdateBooking = (booking: any) => {
      return ![BOOKING_STATUSES.complete, BOOKING_STATUSES.cancel].includes(booking.status);
    }
    const canCancelBooking = (booking: any) => {
      return ![BOOKING_STATUSES.complete, BOOKING_STATUSES.cancel].includes(booking.status);
    }
    const isBookingCompleted = (booking: any) => {
      return booking.status == BOOKING_STATUSES.complete;
    }

    const showBookingQRCode = async (booking: any) => {
      const alert = await alertController.create({
        header: t('bookingQRCode'),
        message: `<ion-img src="https://qrcode.tec-it.com/API/QRCode?data=${booking.id}&dpi=96"></ion-img>`,
        buttons: [
          {
            text: t('back'),
            role: 'cancel',
            cssClass: 'secondary'
          },
        ],
      });
      return alert.present();
    }

    // 3. return variables & methods to be used in template HTML
    return {
      // methods
      t, formatDate, cancelBooking, canUpdateBooking, canCancelBooking, getBookingStatusColor, showBookingQRCode, isBookingCompleted,

      // icons
      star, starHalfOutline, starOutline, pencil, close, calendarOutline, qrCodeOutline,

      // variables
      loading, booking,
    };
  },
};
</script>

<style scoped>
</style>